import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCheck, Undo2, FileText, Wallet, Loader2, FileSpreadsheet, FileDown, Eye, BadgeCheck, AlertTriangle } from "lucide-react";
import { BayarBadge, BayarDialog, DetailBayarDialog, BayarSemuaDialog } from "../components/BayarDialogs";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { EmptyState, ConfirmDialog, StatusBadge } from "../components/common";
import { PerusahaanSelect, PeriodPicker } from "../components/inputs";
import { api, errMsg, downloadFile } from "../lib/api";
import { currentPeriod, fmtDate, num, rp } from "../lib/format";

const COLS = [
  ["voucher", "Voucher", (v) => num(v), "text-gray-900"],
  ["gaji_kotor", "Gaji kotor", rp, "text-gray-900 font-semibold"],
  ["kasbon_sebelumnya", "Kasbon lalu", rp, "text-gray-500"],
  ["kasbon_periode", "Kasbon periode", rp, "text-gray-500"],
  ["potongan", "Potongan", rp, "text-red-600"],
  ["gaji_bersih", "Gaji bersih", rp, "text-gray-900 font-bold"],
  ["sisa_kasbon", "Sisa kasbon", rp, "text-amber-700"],
];
const KEY_COLS = COLS.filter(([k]) => !k.startsWith("kasbon_"));
const GRID = "grid-cols-[minmax(9.5rem,1.6fr)_3.25rem_repeat(5,minmax(6rem,1fr))_8rem_11.25rem] gap-x-2";
const TABLE_COLS = [COLS[0], COLS[1], ["kasbon", "Kasbon lalu / periode"], ...COLS.slice(4)];

const Cells = ({ v, id, total }) => TABLE_COLS.map(([k, , f, tone]) => k === "kasbon" ? (
  <div key={k} className="pr-2 text-right text-[13px] leading-snug tabular-nums text-gray-500">
    <p data-testid={`gajian-${total ? "total-row-" : ""}kasbon_sebelumnya${id}`} className="whitespace-nowrap">lalu {rp(v.kasbon_sebelumnya)}</p>
    <p data-testid={`gajian-${total ? "total-row-" : ""}kasbon_periode${id}`} className="whitespace-nowrap">baru {rp(v.kasbon_periode)}</p>
  </div>
) : <p key={k} data-testid={`gajian-${total ? "total-row-" : ""}${k}${id}`} className={`${NUM} ${total ? "font-bold" : ""} ${tone}`}>{f(v[k])}</p>);
const NUM = "whitespace-nowrap text-right text-sm font-semibold tabular-nums";

const PayInfo = ({ r }) => (
  <>
    {r.bayar.status === "sudah" && <span className="text-xs text-gray-500">{fmtDate(r.bayar.tanggal_bayar)} · {r.bayar.cara_label}</span>}
    {r.bayar.bukti_belum && <span data-testid={`gajian-bukti-belum-${r.anak_id}`} className="text-xs font-semibold text-amber-700">bukti belum diunggah</span>}
    {r.cek_ulang && <CekUlang r={r} />}
  </>
);

const RowActions = ({ r, period, onBayar, onDetail, m = "" }) => {
  const paid = r.bayar.status === "sudah";
  const cls = "h-11 flex-1 text-[15px] xl:h-9 xl:flex-none xl:px-3 xl:text-sm";
  return (
    <div className="flex gap-2 xl:justify-end xl:gap-1.5">
      {paid ? (
        <Button data-testid={`gajian-bayar-detail${m}-${r.anak_id}`} variant="outline" className={cls} title="Lihat detail pembayaran" onClick={() => onDetail(r)}><Eye className="mr-1.5 h-4 w-4" />Detail</Button>
      ) : (
        <Button data-testid={`gajian-bayar-btn${m}-${r.anak_id}`} className={`${cls} bg-emerald-600 hover:bg-emerald-500`} title="Tandai anak ini sudah dibayar" onClick={() => onBayar(r)}><BadgeCheck className="mr-1.5 h-4 w-4" />{m ? "Tandai Dibayar" : "Bayar"}</Button>
      )}
      <Button asChild variant="outline" className={cls}>
        <Link to={`/slip/${period}/${r.anak_id}`} data-testid={`gajian-slip${m}-${r.anak_id}`} title="Lihat slip gaji"><FileText className="mr-1.5 h-4 w-4" />Slip</Link>
      </Button>
    </div>
  );
};

const NameCell = ({ r }) => (
  <div className="min-w-0">
    <Link to={`/anak/${r.anak_id}`} className="break-words text-[15px] font-semibold leading-snug text-gray-900 hover:underline">{r.anak_nama}</Link>
    <p className="break-words text-[13px] leading-snug text-gray-500">{r.perusahaan_nama || "Tanpa perusahaan"}</p>
  </div>
);

const Row = (props) => {
  const { r, onDetail } = props;
  const paid = r.bayar.status === "sudah";
  return (
    <div data-testid={`gajian-row-${r.anak_id}`} className={`grid min-h-[3.25rem] items-center border-b border-gray-100 px-3 py-2 last:border-0 ${paid ? "bg-emerald-50/40" : ""} ${GRID}`}>
      <NameCell r={r} />
      <Cells v={r} id={`-${r.anak_id}`} />
      <div className="flex flex-col items-end gap-0.5">
        <BayarBadge bayar={r.bayar} testid={`gajian-bayar-status-${r.anak_id}`} onClick={paid ? () => onDetail(r) : undefined} />
        <PayInfo r={r} />
      </div>
      <RowActions {...props} />
    </div>
  );
};

const Card = (props) => {
  const { r, onDetail } = props;
  const paid = r.bayar.status === "sudah";
  return (
    <div data-testid={`gajian-card-${r.anak_id}`} className={`rounded-xl border border-gray-200 p-4 ${paid ? "bg-emerald-50/40" : "bg-white"}`}>
      <div className="flex items-start justify-between gap-3">
        <NameCell r={r} />
        <BayarBadge bayar={r.bayar} testid={`gajian-bayar-status-m-${r.anak_id}`} onClick={paid ? () => onDetail(r) : undefined} />
      </div>
      <div className="mt-1 flex flex-wrap gap-x-2"><PayInfo r={r} /></div>
      <dl className="my-3 grid grid-cols-2 gap-x-4 gap-y-2">
        {KEY_COLS.map(([k, label, f, tone]) => (
          <div key={k}>
            <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</dt>
            <dd data-testid={`gajian-m-${k}-${r.anak_id}`} className={`text-[15px] font-semibold tabular-nums ${tone}`}>{f(r[k])}</dd>
          </div>
        ))}
      </dl>
      <RowActions {...props} m="-m" />
    </div>
  );
};

const CekUlang = ({ r }) => (
  <span data-testid={`gajian-cek-ulang-${r.anak_id}`} title={`Kasbon sebelumnya tercatat ${rp(r.cek_ulang.kasbon_sebelumnya_tercatat)}, sekarang ${rp(r.cek_ulang.kasbon_sebelumnya_sekarang)}. Jadikan Belum Dibayar lalu bayar ulang untuk memperbarui.`}
    className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold uppercase text-amber-800">
    <AlertTriangle className="h-3 w-3" />Perlu dicek ulang: kasbon sebelumnya {rp(r.cek_ulang.kasbon_sebelumnya_tercatat)} → {rp(r.cek_ulang.kasbon_sebelumnya_sekarang)}
  </span>
);

const Stat = ({ label, value, tone = "text-gray-900", testid }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
    <p data-testid={testid} className={`mt-1 whitespace-nowrap text-lg font-semibold tabular-nums sm:text-xl ${tone}`}>{value}</p>
  </div>
);

const Summary = ({ s, t }) => {
  const pct = s.jumlah_anak ? Math.round((s.sudah / s.jumlah_anak) * 100) : 0;
  return (
    <div data-testid="gajian-ringkasan-bayar" className="fade-up mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div className="col-span-2 rounded-xl border border-gray-200 bg-white px-4 py-3 lg:col-span-1">
        <p data-testid="gajian-ringkasan-text" className="text-[15px] font-semibold text-gray-900">{s.sudah} dari {s.jumlah_anak} anak sudah dibayar</p>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-red-100"><div className="h-full rounded-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
        {s.bukti_belum > 0 && <p data-testid="gajian-ringkasan-bukti-belum" className="mt-1 text-xs font-semibold text-amber-700">{s.bukti_belum} transfer: bukti belum diunggah</p>}
      </div>
      <Stat label="Sudah dibayar" value={rp(s.rp_sudah)} tone="text-emerald-600" testid="gajian-ringkasan-sudah-rp" />
      <Stat label="Belum dibayar" value={rp(s.rp_belum)} tone="text-red-600" testid="gajian-ringkasan-belum-rp" />
      <Stat label="Total voucher" value={num(t.voucher)} testid="gajian-total-voucher" />
      <Stat label="Gaji kotor" value={rp(t.gaji_kotor)} testid="gajian-total-gaji_kotor" />
      <Stat label="Potongan kasbon" value={rp(t.potongan)} tone="text-red-600" testid="gajian-total-potongan" />
      <Stat label="Gaji bersih" value={rp(t.gaji_bersih)} tone="text-gray-900" testid="gajian-total-gaji_bersih" />
      <Stat label="Sisa kasbon" value={rp(t.sisa_kasbon)} tone="text-amber-700" testid="gajian-total-sisa_kasbon" />
    </div>
  );
};

const FILTER_BAYAR = [["", "Semua"], ["sudah", "Sudah Dibayar"], ["belum", "Belum Dibayar"]];

export default function GajianPage() {
  const [period, setPeriod] = useState(currentPeriod());
  const [perId, setPerId] = useState("");
  const [perusahaan, setPerusahaan] = useState([]);
  const [d, setD] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState("");
  const [fBayar, setFBayar] = useState("");
  const [bayarRow, setBayarRow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  useEffect(() => { api.get("/perusahaan").then((r) => setPerusahaan(r.data)); }, []);
  const load = useCallback(() => {
    api.get("/payroll", { params: { period, perusahaan_id: perId || undefined, status_bayar: fBayar || undefined } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [period, perId, fBayar]);
  useEffect(() => { load(); }, [load]);

  const act = async (body) => {
    const kind = confirm;
    setConfirm(null);
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/${period}/${kind}`, kind === "pay" ? body : undefined);
      (data.peringatan || []).forEach((w) => toast.warning(w, { duration: 8000 }));
      toast.success(kind === "pay" ? "Semua anak ditandai dibayar — periode LUNAS" : "Pembayaran dibatalkan, semua anak kembali Belum Dibayar");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const t = d?.totals || {};
  const askPay = () => setConfirm("pay");
  const askBayar = setBayarRow;
  const rb = d?.ringkasan_bayar || { jumlah_anak: 0, sudah: 0, rp_sudah: 0, rp_belum: 0 };
  const periodLabel = d?.status === "paid" ? "Lunas — Sudah Dibayar" : rb.sudah > 0 ? `Sebagian dibayar (${rb.sudah}/${rb.jumlah_anak})` : "Belum dibayar";
  const download = async (ext) => {
    setExporting(ext);
    try {
      await downloadFile(`/payroll/${period}/export/${ext}`, { perusahaan_id: perId || undefined }, `Rekap_Gajian_CLOVER.${ext}`);
      toast.success(`File ${ext === "xlsx" ? "Excel" : "PDF"} diunduh`);
    } catch (e) { toast.error(errMsg(e, "Gagal mengunduh file")); } finally { setExporting(""); }
  };
  const exportBtns = d && (
    <>
      <Button data-testid="gajian-export-xlsx" variant="outline" title="Unduh rekap Excel" disabled={!!exporting} className="h-10 rounded-full px-4" onClick={() => download("xlsx")}>
        {exporting === "xlsx" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1.5 h-4 w-4 text-emerald-700" />}Excel
      </Button>
      <Button data-testid="gajian-export-pdf" variant="outline" title="Unduh rekap PDF" disabled={!!exporting} className="h-10 rounded-full px-4" onClick={() => download("pdf")}>
        {exporting === "pdf" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4 text-red-600" />}PDF
      </Button>
    </>
  );
  const rows = d?.rows || [];
  const rowProps = { period, onBayar: askBayar, onDetail: setDetailRow };
  return (
    <div>
      <div className="fade-up mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 data-testid="gajian-title" className="font-heading text-[26px] font-semibold leading-tight text-gray-900 sm:text-[28px]">Gajian</h1>
          {d && <StatusBadge status={d.status} label={periodLabel} testid="gajian-status" />}
        </div>
        {d && (
          <div className="flex flex-wrap items-center gap-2">
            {exportBtns}
            {(d.status === "paid" || rb.sudah > 0) && (
              <Button data-testid="gajian-unpay-btn" variant="outline" disabled={busy} title="Batalkan pembayaran semua anak di periode ini" className="h-10 rounded-full border-red-200 px-4 text-red-700 hover:bg-red-50" onClick={() => setConfirm("unpay")}><Undo2 className="mr-1.5 h-4 w-4" />Batalkan</Button>
            )}
            {d.status !== "paid" && (
              <Button data-testid="gajian-pay-btn" disabled={busy || rb.sudah >= rb.jumlah_anak} title="Tandai semua anak sudah dibayar" className="h-10 rounded-full px-5" onClick={askPay}>
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCheck className="mr-1.5 h-4 w-4" />}Bayar Semua
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
        <div className="lg:w-80"><PeriodPicker value={period} onChange={setPeriod} testid="gajian-period" /></div>
        <div className="lg:w-60"><PerusahaanSelect perusahaan={perusahaan} value={perId} onChange={setPerId} allowAll testid="gajian-filter-perusahaan" /></div>
        <div className="flex h-11 rounded-lg border border-gray-200 bg-white p-1 sm:col-span-2 md:h-10 lg:col-span-1">
          {FILTER_BAYAR.map(([v, l]) => (
            <button key={v || "semua"} data-testid={`gajian-filter-bayar-${v || "semua"}`} onClick={() => setFBayar(v)}
              className={`flex-1 whitespace-nowrap rounded-md px-3 text-[13px] font-semibold transition-colors lg:flex-none ${fBayar === v ? (v === "belum" ? "bg-red-600 text-white" : "bg-primary text-white") : "text-gray-600 hover:bg-gray-100"}`}>{l}</button>
          ))}
        </div>
      </div>

      {d && rows.length + rb.jumlah_anak > 0 && <Summary s={rb} t={t} />}

      {d && rows.length === 0 ? <EmptyState icon={Wallet} title="Tidak ada data gajian" desc={fBayar ? "Tidak ada anak dengan status ini." : "Belum ada voucher atau kasbon outstanding untuk periode ini."} /> : (
        <>
          <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white xl:block" data-testid="gajian-table">
            <div className={`grid border-b border-gray-200 bg-gray-50 px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-gray-500 ${GRID}`}>
              <span>Anak</span>{TABLE_COLS.map(([k, l]) => <span key={k} className="text-right">{l}</span>)}<span className="text-right">Status</span><span className="text-right">Aksi</span>
            </div>
            {rows.map((r) => <Row key={r.anak_id} r={r} {...rowProps} />)}
            {rows.length > 0 && (
              <div className={`grid min-h-[3.25rem] items-center border-t-2 border-gray-300 bg-emerald-50/70 px-3 py-2 ${GRID}`} data-testid="gajian-total-row">
                <span className="text-sm font-semibold uppercase tracking-wider text-gray-900">TOTAL ({rows.length} anak)</span>
                <Cells v={t} id="" total /><span /><span />
              </div>
            )}
          </div>
          <div className="space-y-3 xl:hidden" data-testid="gajian-cards">
            <div className="grid gap-3 md:grid-cols-2">{rows.map((r) => <Card key={r.anak_id} r={r} {...rowProps} />)}</div>
            {rows.length > 0 && (
              <div data-testid="gajian-total-card" className="sticky bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-20 rounded-2xl border border-rose-100 bg-white/95 px-4 py-3 shadow-[0_10px_30px_-10px_rgba(16,24,40,0.25)] backdrop-blur-md md:bottom-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold uppercase tracking-wider text-gray-900">TOTAL ({rows.length} anak)</p>
                  <p data-testid="gajian-total-card-bersih" className="text-lg font-bold tabular-nums text-gray-900">{rp(t.gaji_bersih)}</p>
                </div>
                <p className="mt-0.5 text-[13px] tabular-nums text-gray-500">{num(t.voucher)} voucher · kotor {rp(t.gaji_kotor)} · potongan {rp(t.potongan)}</p>
              </div>
            )}
          </div>
        </>
      )}

      <BayarSemuaDialog open={confirm === "pay"} onOpenChange={(o) => !o && setConfirm(null)} sisa={rb.jumlah_anak - rb.sudah} sisaRp={rb.rp_belum} onConfirm={act} />
      <BayarDialog open={!!bayarRow} row={bayarRow} period={period} onOpenChange={(o) => !o && setBayarRow(null)} onDone={load} />
      <DetailBayarDialog open={!!detailRow} row={detailRow && { ...detailRow, lunas: d?.status === "paid" }} period={period} onOpenChange={(o) => !o && setDetailRow(null)} onDone={load}
        canRevert />
      <ConfirmDialog open={confirm === "unpay"} onOpenChange={(o) => !o && setConfirm(null)} destructive title="Batalkan pembayaran semua anak?"
        description={d ? `Semua anak di ${d.label} akan kembali menjadi Belum Dibayar (catatan & bukti pembayaran dihapus) dan data periode ini bisa diubah lagi. Angka akan dihitung ulang dengan tarif saat ini.` : ""}
        confirmLabel="Ya, batalkan" onConfirm={act} testid="gajian-unpay-confirm" />
    </div>
  );
}
